import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ClubService from '../services/clubService';
import { useClubs, useApp } from '../context/AppContext';

interface QuizQuestion {
  question: string;
  type: 'mcq' | 'paragraph';
  options: string[];
  correctAnswer: number;
}

const CreateClubPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshClubs } = useClubs();
  const { refreshUser } = useApp();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Club Basic Info
  const [clubName, setClubName] = useState('');
  const [clubDescription, setClubDescription] = useState('');
  const [clubLogo, setClubLogo] = useState<File | null>(null);
  const [clubBanner, setClubBanner] = useState<File | null>(null);

  // Quiz Questions
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([
    {
      question: '',
      type: 'mcq',
      options: ['', '', '', ''],
      correctAnswer: 0,
    },
  ]);

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateCurrentStep = () => {
    setError(null);

    switch (activeStep) {
      case 0:
        if (!clubName.trim()) {
          setError('Club name is required');
          return false;
        }
        if (!clubDescription.trim()) {
          setError('Club description is required');
          return false;
        }
        if (clubDescription.length < 50) {
          setError('Club description must be at least 50 characters long');
          return false;
        }
        if (clubDescription.length > 1000) {
          setError('Club description must not exceed 1000 characters');
          return false;
        }
        break;
      case 1:
        if (quizQuestions.length < 1) {
          setError('At least 1 quiz question is required');
          return false;
        }
        for (let i = 0; i < quizQuestions.length; i++) {
          const q = quizQuestions[i];
          if (!q.question.trim()) {
            setError(`Question ${i + 1} is required`);
            return false;
          }
          if (q.type === 'mcq') {
            const validOptions = q.options.filter(opt => opt && opt.trim());
            if (validOptions.length < 2) {
              setError(`Question ${i + 1}: MCQ questions must have at least 2 options filled in`);
              return false;
            }
          }
        }
        break;
    }
    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'banner') => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return;
      }
      if (type === 'logo') {
        setClubLogo(file);
      } else {
        setClubBanner(file);
      }
    }
  };

  const addQuizQuestion = (type: 'mcq' | 'paragraph' = 'mcq') => {
    setQuizQuestions([
      ...quizQuestions,
      {
        question: '',
        type: type,
        options: type === 'mcq' ? ['', '', '', ''] : [],
        correctAnswer: 0,
      },
    ]);
  };

  const removeQuizQuestion = (index: number) => {
    if (Array.isArray(quizQuestions) && quizQuestions.length > 1) {
      const newQuestions = quizQuestions.filter((_, i) => i !== index);
      setQuizQuestions(newQuestions);
    }
  };

  const updateQuizQuestion = (index: number, field: keyof QuizQuestion, value: any) => {
    const newQuestions = [...quizQuestions];
    if (field === 'options') {
      newQuestions[index].options = value;
    } else if (field === 'type') {
      // When changing type, reset options appropriately
      newQuestions[index].type = value;
      newQuestions[index].options = value === 'mcq' ? ['', '', '', ''] : [];
      newQuestions[index].correctAnswer = 0;
    } else {
      (newQuestions[index] as any)[field] = value;
    }
    setQuizQuestions(newQuestions);
  };

  const updateQuizOption = (questionIndex: number, optionIndex: number, value: string) => {
    const newQuestions = [...quizQuestions];
    newQuestions[questionIndex].options[optionIndex] = value;
    setQuizQuestions(newQuestions);
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    try {
      setLoading(true);
      setError(null);

      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('name', clubName);
      formData.append('description', clubDescription);
      formData.append('tags', JSON.stringify([]));
      formData.append('quiz', JSON.stringify({
        passingScore: 70,
        questions: quizQuestions.map(q => ({
          question: q.question,
          type: q.type,
          options: q.options,
          correctAnswer: q.correctAnswer,
        })),
      }));

      if (clubLogo) {
        formData.append('logo', clubLogo);
      }
      if (clubBanner) {
        formData.append('banner', clubBanner);
      }

      // For now, create basic data without files until we implement file upload
      const clubData = {
        name: clubName,
        description: clubDescription,
        logo: clubLogo ? 'uploaded-logo.jpg' : '', // Placeholder for file upload
        banner: clubBanner ? 'uploaded-banner.jpg' : '', // Placeholder for file upload
        tags: [], // Add empty tags array as required by interface
        quiz: {
          passingScore: 70, // Default passing score
          questions: quizQuestions.map(q => ({
            question: q.question,
            type: q.type,
            options: q.options,
            correctAnswer: q.correctAnswer,
          })),
        },
      };

      const newClub = await ClubService.createClub(clubData);

      // Refresh clubs list and user data to show the new club with proper permissions
      await Promise.all([
        refreshClubs(),
        refreshUser()
      ]);

      // Redirect to the newly created club's details page
      navigate(`/clubs/${newClub.id}`);
    } catch (error: any) {
      console.error('Failed to create club:', error);
      setError('Failed to create club. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Creating your club..." />;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, fontSize: { xs: '1.75rem', sm: '3rem' } }}>
          Create New Club
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
          Set up your club with basic information and entry requirements
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Card sx={{ overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 1: Basic Information */}
            <Step>
              <StepLabel>Basic Information</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Club Name"
                    placeholder="Enter your club name"
                    value={clubName}
                    onChange={(e) => setClubName(e.target.value)}
                    sx={{ mb: 3 }}
                    required
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Club Description"
                    placeholder="Describe your club's purpose, activities, and goals (50-1000 characters required)"
                    value={clubDescription}
                    onChange={(e) => setClubDescription(e.target.value)}
                    sx={{ mb: 3 }}
                    required
                    error={clubDescription.length > 0 && (clubDescription.length < 50 || clubDescription.length > 1000)}
                    helperText={
                      clubDescription.length < 50
                        ? `${clubDescription.length}/50 minimum characters (${50 - clubDescription.length} more needed)`
                        : clubDescription.length > 1000
                        ? `${clubDescription.length}/1000 maximum characters (${clubDescription.length - 1000} over limit)`
                        : `${clubDescription.length}/1000 characters (valid ✓)`
                    }
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Club Logo (Optional)
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                        fullWidth
                        sx={{ p: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {clubLogo ? clubLogo.name : 'Upload Logo'}
                        </Box>
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'logo')}
                        />
                      </Button>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Club Banner (Optional)
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                        fullWidth
                        sx={{ p: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {clubBanner ? clubBanner.name : 'Upload Banner'}
                        </Box>
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'banner')}
                        />
                      </Button>
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>Requirements:</strong>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Club name is required</li>
                      <li>Description must be between 50-1000 characters</li>
                      <li>Logo and banner are optional but recommended</li>
                    </ul>
                  </Alert>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mr: 1 }}
                    >
                      Continue
                    </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>

            {/* Step 2: Create Quiz */}
            <Step>
              <StepLabel>Create Entry Quiz</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Create quiz questions to evaluate potential members. You can add MCQ or paragraph type questions.
                  </Alert>

                  {quizQuestions.map((question, questionIndex) => (
                    <Card key={questionIndex} variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: { xs: 1, sm: 0 } }}>
                          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            Question {questionIndex + 1}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip
                              label={question.type === 'mcq' ? 'MCQ' : 'Paragraph'}
                              size="small"
                              color={question.type === 'mcq' ? 'primary' : 'secondary'}
                              onClick={() => updateQuizQuestion(questionIndex, 'type', question.type === 'mcq' ? 'paragraph' : 'mcq')}
                              sx={{ cursor: 'pointer' }}
                            />
                            {quizQuestions.length > 1 && (
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => removeQuizQuestion(questionIndex)}
                              >
                                <RemoveIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          label="Question"
                          placeholder="Enter your question"
                          value={question.question}
                          onChange={(e) => updateQuizQuestion(questionIndex, 'question', e.target.value)}
                          sx={{ mb: 3 }}
                          required
                        />

                        {question.type === 'mcq' ? (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>
                              Answer Options:
                            </Typography>

                            {question.options.map((option, optionIndex) => (
                              <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ width: 24, mr: 2, textAlign: 'center' }}>
                                  <input
                                    type="radio"
                                    name={`question-${questionIndex}`}
                                    checked={question.correctAnswer === optionIndex}
                                    onChange={() => updateQuizQuestion(questionIndex, 'correctAnswer', optionIndex)}
                                  />
                                </Box>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder={`Option ${optionIndex + 1}`}
                                  value={option}
                                  onChange={(e) => updateQuizOption(questionIndex, optionIndex, e.target.value)}
                                  required
                                />
                              </Box>
                            ))}

                            <Typography variant="caption" color="text.secondary">
                              Select the correct answer by clicking the radio button
                            </Typography>
                          </>
                        ) : (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            This is a paragraph answer question. Students will provide written responses that will need manual review.
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  ))}

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => addQuizQuestion('mcq')}
                      fullWidth
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Add MCQ Question
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => addQuizQuestion('paragraph')}
                      fullWidth
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Add Paragraph Question
                    </Button>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mr: 1 }}
                    >
                      Continue
                    </Button>
                    <Button onClick={handleBack}>
                      Back
                    </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>

            {/* Step 3: Review & Submit */}
            <Step>
              <StepLabel>Review & Submit</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Review Your Club Details
                  </Typography>

                  <Card variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Club Information
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
                        <strong>Name:</strong> {clubName}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
                        <strong>Description:</strong> {clubDescription}
                      </Typography>
                      {clubLogo && (
                        <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          <strong>Logo:</strong> {clubLogo.name}
                        </Typography>
                      )}
                      {clubBanner && (
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          <strong>Banner:</strong> {clubBanner.name}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Entry Quiz ({quizQuestions.length} questions)
                      </Typography>
                      {quizQuestions.map((question, index) => (
                        <Box key={index} sx={{ mb: 2, p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word' }}>
                            {index + 1}. {question.question}
                          </Typography>
                          {question.type === 'mcq' && question.options.map((option, optionIndex) => (
                            <Typography
                              key={optionIndex}
                              variant="body2"
                              sx={{
                                color: question.correctAnswer === optionIndex ? 'success.main' : 'text.secondary',
                                fontWeight: question.correctAnswer === optionIndex ? 600 : 400,
                                wordBreak: 'break-word'
                              }}
                            >
                              {String.fromCharCode(65 + optionIndex)}. {option}
                              {question.correctAnswer === optionIndex && ' ✓'}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </CardContent>
                  </Card>

                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Once you create the club, you'll be able to manage member applications and moderate discussions.
                    Make sure all information is correct before submitting.
                  </Alert>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSubmit}
                      sx={{ mr: 1 }}
                      disabled={loading}
                    >
                      {loading ? 'Creating Club...' : 'Create Club'}
                    </Button>
                    <Button onClick={handleBack} disabled={loading}>
                      Back
                    </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CreateClubPage;